import { isFunction } from "../../frontendlib/utils";
import {
  MRAID_STATE_CHANGE_EVENTS,
  MRAID_STATE_LOADING,
  MRAID_EVENT_EXPOSURE_CHANGE,
} from "./mraid_constants";

let lastMraid = window.mraid;
/**
 * Returns the mraid object. In some environments, it appears that the mraid
 * object gets removed as the ad is loading. See
 * https://docs.google.com/document/d/1TWLnTFlTGFc6ZJB_UXfR8QvmEa8x_d3k532fmX-13Fs
 * and https://docs.google.com/document/d/1iChTG_zLZvQzI5Opw-6kpHqRCXRYklTPpbNB7TPBFdQ.
 */
const getMraid = () => {
  lastMraid = window.mraid || lastMraid;
  return lastMraid;
};

/**
 * Returns true iff mraid is ready.
 */
const isMraidReady = () => {
  const mraid = getMraid();
  return (
    mraid != null &&
    mraid.getState != null &&
    isFunction(mraid.getState) &&
    mraid.getState() !== MRAID_STATE_LOADING
  );
};

// onError must receive (errorMessage, methodName).
export const callMraidMethod = (methodName, args = [], onError) => {
  const mraid = getMraid();

  const handleError = (errorMessage) => {
    // Suppress this warning in tests to avoid cluttering test output.
    // #if process.env.NODE_ENV !== "test"
    console.warn(`Error calling MRAID method '${methodName}': ${errorMessage}`);
    // #endif
    if (onError == null) return;
    onError(errorMessage, methodName);
  };

  if (mraid == null) {
    handleError("MRAID is not available.");
    return null;
  }

  // According to the mraid spec, getState and addEventListener should be
  // available even before mraid is ready.
  if (
    methodName !== "getState" &&
    methodName !== "addEventListener" &&
    !isMraidReady()
  ) {
    handleError(`MRAID.${methodName} is called before MRAID is ready.`);
    return null;
  }

  if (!isFunction(mraid[methodName])) {
    handleError(`MRAID.${methodName} is not supported in this environment.`);
    return null;
  }

  try {
    return mraid[methodName](...args);
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : `${e}`;
    handleError(errorMessage);
    return null;
  }
};

const addEventListener = (eventName, cb) => {
  if (eventName === MRAID_EVENT_EXPOSURE_CHANGE && getVersion() < 3) {
    return;
  }

  callMraidMethod("addEventListener", [eventName, cb]);
};

/**
 * Synchronously runs callback cb if mraid is ready; otherwise runs cb when
 * mraid is ready.
 */
const onMraidReady = (cb) => {
  if (isMraidReady()) {
    cb();
    return;
  }

  addEventListener("ready", cb);
};

const doesMraidSupport = (feature) => {
  return callMraidMethod("supports", [feature]);
};

const querySupports = () => {
  const isSupportedByProperty = [
    "sms",
    "tel",
    "calendar",
    "storePicture",
    "inlineVideo",
    "vpaid",
    "location",
  ].reduce((isSupportedByFeature, property) => {
    isSupportedByFeature[property] = doesMraidSupport(property);
    return isSupportedByFeature;
  }, {});

  return isSupportedByProperty;
};

/**
 * Returns mraid version as a Number, or 0 if the mraid version is
 * unavailable or cannot be parsed.
 */
const getVersion = () => {
  const version = parseFloat(callMraidMethod("getVersion"));
  return Number.isNaN(version) ? 0 : version;
};

const getOrientationProperties = () => {
  return callMraidMethod("getOrientationProperties");
};

const getCurrentAppOrientation = () => {
  return getVersion() < 3 ? null : callMraidMethod("getCurrentAppOrientation");
};

const getCurrentPosition = () => {
  return callMraidMethod("getCurrentPosition");
};

const getDefaultPosition = () => {
  return callMraidMethod("getDefaultPosition");
};

const getExpandProperties = () => {
  return callMraidMethod("getExpandProperties");
};

const getMaxSize = () => {
  return callMraidMethod("getMaxSize");
};

const getScreenSize = () => {
  return callMraidMethod("getScreenSize");
};

const getResizeProperties = () => {
  return callMraidMethod("getResizeProperties");
};

const getLocation = () => {
  return getVersion() < 3 || !doesMraidSupport("location")
    ? { lat: -1, lon: -1 }
    : callMraidMethod("getLocation");
};

const getIsViewable = () => {
  return callMraidMethod("isViewable");
};

const getState = () => {
  return callMraidMethod("getState");
};

let currentExposure = null;
const updateCurrentExposure = (
  exposedPercentage,
  visibleRectangle,
  occlusionRectangles
) => {
  currentExposure = {
    exposedPercentage,
    visibleRectangle,
    occlusionRectangles,
  };
};

export const initOuterMraid = () => {
  onMraidReady(() => {
    addEventListener(MRAID_EVENT_EXPOSURE_CHANGE, updateCurrentExposure);
  });
};

export const getMraidProperties = () => {
  const mraidState = getState();

  return {
    isViewable: getIsViewable(),
    supports: querySupports(),
    orientationProperties: getOrientationProperties(),
    currentAppOrientation: getCurrentAppOrientation(),
    currentPosition: getCurrentPosition(),
    defaultPosition: getDefaultPosition(),
    expandProperties: getExpandProperties(),
    maxSize: getMaxSize(),
    screenSize: getScreenSize(),
    resizeProperties: getResizeProperties(),
    location: getLocation(),
    state: mraidState,
    isAvailable: mraidState != null,
    currentExposure,
  };
};

export const onMraidStateChangeEvent = (callback) => {
  MRAID_STATE_CHANGE_EVENTS.forEach((eventName) => {
    const eventCallback = (...args) => {
      callback(eventName, ...args);
    };
    addEventListener(eventName, eventCallback);
  });
};

// #if process.env.NODE_ENV === "test"
// export {
//   addEventListener,
//   getExpandProperties,
//   getLocation,
//   getMraid,
//   getVersion,
//   isMraidReady,
//   onMraidReady,
// };
// #endif



// WEBPACK FOOTER //
// ./src/js/madlib/csp-env/outer_mraid.js